import type {
  PublicHolidayCreate,
  PublicHolidayImportRequest,
  PublicHolidayImportSubdivision,
  PublicHolidaySuggestion,
} from '../../../api/attendance';

export interface HolidayLocation {
  id: string;
  name: string;
  country?: string | null;
  state?: string | null;
}

export interface RegionInference {
  subdivision: string;
  needsConfirmation: boolean;
}

function normalized(value: string): string {
  return value.trim().toLocaleLowerCase().replace(/[\s._-]+/g, '');
}

export function inferHolidayImportRegion(
  country: string,
  state: string | null | undefined,
  provider: 'openholidays' | 'nager_date' | undefined,
  subdivisions: PublicHolidayImportSubdivision[],
): RegionInference {
  const countryCode = country.trim().toUpperCase();
  const rawState = state?.trim() ?? '';
  if (!countryCode || !rawState) return { subdivision: '', needsConfirmation: false };

  if (subdivisions.length > 0) {
    const stateKey = normalized(rawState);
    const match = subdivisions.find((item) => {
      const code = item.code.toUpperCase();
      const shortCode = code.includes('-') ? code.split('-').slice(1).join('-') : code;
      return [item.name, code, shortCode].some((candidate) => normalized(candidate) === stateKey);
    });
    return match
      ? { subdivision: match.code.toUpperCase(), needsConfirmation: false }
      : { subdivision: '', needsConfirmation: provider === 'openholidays' };
  }

  if (provider === 'nager_date') {
    const upper = rawState.toUpperCase();
    const fullCode = upper.match(/^([A-Z]{2})-([A-Z0-9]{1,8})$/);
    if (fullCode && fullCode[1] === countryCode) {
      return { subdivision: upper, needsConfirmation: false };
    }
    if (/^[A-Z0-9]{1,3}$/.test(upper)) {
      return { subdivision: `${countryCode}-${upper}`, needsConfirmation: false };
    }
  }

  return { subdivision: '', needsConfirmation: false };
}

export function suggestionKey(item: PublicHolidaySuggestion): string {
  return `${item.source}:${item.source_reference}`;
}

export function defaultHolidaySelection(items: PublicHolidaySuggestion[]): Set<string> {
  return new Set(items.filter((item) => !item.already_exists).map(suggestionKey));
}

export function buildHolidayImportPayload(args: {
  locationId: string;
  country: string;
  subdivision?: string;
  language: string;
  year: number;
  suggestions: PublicHolidaySuggestion[];
  selected: Set<string>;
}): PublicHolidayImportRequest {
  return {
    location_id: args.locationId,
    country: args.country.toUpperCase(),
    subdivision: args.subdivision?.trim().toUpperCase() || null,
    language: args.language.toUpperCase(),
    year: args.year,
    items: args.suggestions
      .filter((item) => !item.already_exists && args.selected.has(suggestionKey(item)))
      .map(({ name, date, source, source_reference }) => ({
        name,
        date,
        source,
        source_reference,
      })),
  };
}

export function buildManualHolidayPayload(args: {
  locationId: string;
  name: string;
  date: string;
  recurring: boolean;
}): PublicHolidayCreate {
  return {
    location_id: args.locationId,
    name: args.name.trim(),
    date: args.date,
    recurring: args.recurring,
  };
}
